import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../housestyle.css";
import "./Blogs.css";
import BLOGS from "../data/Blogs";
import api from "../API/Api";

const TAG_MAP = {
  new: "tag-new",
  fix: "tag-fix",
  change: "tag-change",
  event: "tag-event",
};
const TIER_CLASS = {
  // Mobs
  tier_1: "tier-common",
  tier_2: "tier-uncommon",
  tier_3: "tier-rare",
  tier_4: "tier-boss",
  // Weapon
  common: "tier-common",
  uncommon: "tier-uncommon",
  rare: "tier-rare",
  epic: "tier-epic",
  legendary: "tier-legendary",
  boss: "tier-boss",
};

const TIER_LABEL = {
  tier_1: "Tier 1",
  tier_2: "Tier 2",
  tier_3: "Tier 3",
  tier_4: "Tier 4",
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function parseMd(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br />");
}

function BlockParagraph({ block }) {
  return (
    <p
      className="bp-paragraph"
      dangerouslySetInnerHTML={{ __html: parseMd(block.text) }}
    />
  );
}

function BlockHeading({ block }) {
  return <h2 className="bp-heading">{block.text}</h2>;
}

function BlockImages({ block }) {
  return (
    <div
      className={`bp-images bp-images-${block.items.length > 1 ? "multi" : "single"}`}
    >
      {block.items.map((img, i) => (
        <figure key={i} className="bp-image-figure">
          <img src={img.src} alt={img.caption ?? ""} className="bp-image" />
          {img.caption && (
            <figcaption className="bp-caption">{img.caption}</figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

function BlockYoutube({ block }) {
  return (
    <figure className="bp-video-figure">
      <div className="bp-video-wrap">
        <iframe
          src={block.url}
          title={block.caption ?? "Video"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {block.caption && (
        <figcaption className="bp-caption">{block.caption}</figcaption>
      )}
    </figure>
  );
}

function BlockMp4({ block }) {
  return (
    <figure className="bp-video-figure">
      <video
        className="bp-video-mp4"
        src={block.src}
        poster={block.poster ?? undefined}
        controls
        playsInline
      />
      {block.caption && (
        <figcaption className="bp-caption">{block.caption}</figcaption>
      )}
    </figure>
  );
}

function BlockMobCard({ block: mob }) {
  const tierClass = TIER_CLASS[mob.tier] ?? "tier-common";
  const TIER_LABEL = { tier_1: "Tier 1", tier_2: "Tier 2", tier_3: "Tier 3", tier_4: "Tier 4" };
  const tierLabel = TIER_LABEL[mob.tier] ?? mob.tier;
  const abilitiescolor = mob.abilitiescolor ?? "var(--text-primary)";
  const abilities = mob.abilities ?? [];
  const drops = mob.drops ?? [];
  const hpPct = Math.min(100, Math.round(((mob.hp ?? 0) / 3000) * 100));
  const dmgPct = Math.min(100, Math.round(((mob.damage ?? 0) / 80) * 100));

  return (
    <div className="bp-mob-card card-raised">
      <div className="bp-mob-top">
        <span className="bp-mob-emoji">{mob.emoji}</span>
        <div>
          <div className="bp-mob-name">{mob.name}</div>
          <span className={`tier-badge ${tierClass}`}>{tierLabel}</span>
        </div>
      </div>
      <div className="bp-mob-stats">
        <div className="bp-mob-stat">
          <div className="bp-mob-stat-label">
            HP <span>{mob.hp ?? 0}</span>
          </div>
          <div className="stat-bar-track">
            <div
              className="stat-bar-fill"
              style={{ width: `${hpPct}%`, background: "var(--tier-uncommon)" }}
            />
          </div>
        </div>
        <div className="bp-mob-stat">
          <div className="bp-mob-stat-label">
            DMG <span>{mob.damage ?? 0}</span>
          </div>
          <div className="stat-bar-track">
            <div
              className="stat-bar-fill"
              style={{ width: `${dmgPct}%`, background: "var(--tier-boss)" }}
            />
          </div>
        </div>
      </div>
      <div className="bp-mob-row">
        <span className="bp-mob-row-label">Abilities</span>
        <div className="bp-mob-pills">
          {abilities.map((a) => (
            <span key={a} className="ability-pill" style={{ borderColor: abilitiescolor }}>
              {a}
            </span>
          ))}
        </div>
      </div>
      <div className="bp-mob-row">
        <span className="bp-mob-row-label" style={{ color: abilitiescolor }}>
          Drops
        </span>
        <div className="bp-mob-pills">
          {drops.map((d) => (
            <span key={d} className="drop-chip drop-rare" style={{ backgroundColor: abilitiescolor }}>
              {d}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlockItemCard({ block }) {
  const tierClass = TIER_CLASS[block.tier] ?? "tier-common";
  const dmgPct = Math.min(100, Math.round((block.damage / 120) * 100));
  return (
    <div className="bp-mob-card card-raised">
      <div className="bp-mob-top">
        <span className="bp-mob-emoji">{block.emoji}</span>
        <div>
          <div className="bp-mob-name">{block.name}</div>
          <div
            style={{
              display: "flex",
              gap: "6px",
              marginTop: "4px",
              flexWrap: "wrap",
            }}
          >
            <span className={`tier-badge ${tierClass}`}>{block.tier}</span>
            <span
              className="ability-pill"
              style={{ fontSize: "0.62rem", padding: "2px 8px" }}
            >
              {block.type_label}
            </span>
            {block.crafted && (
              <span className="drop-chip drop-uncommon">Craftable</span>
            )}
          </div>
        </div>
      </div>
      {block.lore && (
        <div
          style={{
            fontStyle: "italic",
            fontSize: "var(--t-xs)",
            color: "var(--tier-legendary)",
            borderLeft: "2px solid var(--teal-border)",
            paddingLeft: "10px",
          }}
        >
          "{block.lore}"
        </div>
      )}
      <div className="bp-mob-stats">
        <div className="bp-mob-stat">
          <div className="bp-mob-stat-label">
            Damage <span>{block.damage}</span>
          </div>
          <div className="stat-bar-track">
            <div
              className="stat-bar-fill"
              style={{ width: `${dmgPct}%`, background: "var(--tier-boss)" }}
            />
          </div>
        </div>
        {block.speed && (
          <div className="bp-mob-stat-label" style={{ marginTop: "2px" }}>
            Speed{" "}
            <span style={{ color: "var(--text-primary)" }}>{block.speed}</span>
          </div>
        )}
      </div>
      <div className="bp-mob-row">
        <span className="bp-mob-row-label">Abilities</span>
        <div className="bp-mob-pills">
          {block.abilities.map((a) => (
            <span key={a} className="ability-pill">
              {a}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.type) {
    case "paragraph":
      return <BlockParagraph key={i} block={block} />;
    case "heading":
      return <BlockHeading key={i} block={block} />;
    case "images":
      return <BlockImages key={i} block={block} />;
    case "video_youtube":
      return <BlockYoutube key={i} block={block} />;
    case "video_mp4":
      return <BlockMp4 key={i} block={block} />;
    case "mob_card":
      return <BlockMobCard key={i} block={block} />;
    case "item_card":
      return <BlockItemCard key={i} block={block} />;
    default:
      return null;
  }
}

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const blog = BLOGS.find((b) => String(b.id) === String(id)) ?? null;
  const notFound = !blog;
  const [views, setViews] = useState(null);

  useEffect(() => {
    if (!blog) return;
    const key = `viewed_${blog.id}`;
    if (sessionStorage.getItem(key)) {
      api('getviews', { blog_id: blog.id }).then(res => {
        if (res.isSuccess) setViews(res.data?.views ?? 0);
      });
    } else {
      sessionStorage.setItem(key, '1');
      api('trackview', { blog_id: blog.id }).then(res => {
        if (res.isSuccess) setViews(res.data?.views ?? 0);
      });
    }
  }, [blog?.id]);

  if (notFound)
    return (
      <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📭</div>
        <h2 style={{ fontSize: "var(--t-2xl)", marginBottom: "0.5rem" }}>
          Blog not found
        </h2>
        <p style={{ color: "var(--text-muted)", marginBottom: "1.5rem" }}>
          This blog doesn't exist or was removed.
        </p>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/blogs")}
        >
          ← Back to Blogs
        </button>
      </div>
    );

  return (
    <>
      <div className="bp-hero">
        {blog.thumbnail ? (
          <img src={blog.thumbnail} alt={blog.title} className="bp-hero-img" />
        ) : (
          <div className="bp-hero-blank" />
        )}
        <div className="bp-hero-overlay" />
        <div className="bp-hero-content container">
          <button
            className="btn btn-secondary bp-back"
            onClick={() => navigate("/blogs")}
          >
            ← Blogs
          </button>
          {views !== null && (
            <span className="bp-views">👁 {views} views</span>
          )}
          
          <div className="blog-card-tags bp-hero-tags">
            <div style={{ display: "flex", gap: "8px", marginBottom: "0.5rem", alignItems: "center" }}>
              <span className="blog-card-category">{blog.category}</span>
              {blog.subcategory && (
                <span
                  className="blog-card-category"
                  style={{ background: "rgba(192,57,43,0.75)", color: "#fff" }}
                >
                  {blog.subcategory}
                </span>
              )}
            </div>
            {blog.tags.map((t) => (
              <span key={t} className={`tag ${TAG_MAP[t] ?? ""}`}>
                {t}
              </span>
            ))}
          </div>
          <h1 className="bp-title">{blog.title}</h1>
          <p className="bp-date">{formatDate(blog.date)}</p>
          
        </div>
      </div>

      <div className="container bp-content">
        <div className="bp-summary">{blog.summary}</div>
        <div className="divider" />
        <div className="bp-body">
          {blog.body.map((block, i) => renderBlock(block, i))}
        </div>
        <div className="divider" style={{ marginTop: "2.5rem" }} />
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/blogs")}
        >
          ← Back to all blogs
        </button>
      </div>
    </>
  );
}
